/*
 * Copyright (c) 2022-2022. Revo Digital
 * ---
 * Author: gabriele
 * File: CreateProjectDialog.tsx
 * Project: mle-client-n
 * Committed last: 2022/5/27 @ 1733
 * ---
 * Description:
 */

import classNames from 'classnames';
import {currentThemeIsDark} from '../../../../common/utils';
import {DialogStep, MultistepDialog} from '@blueprintjs/core';
import {FormActionRef, ProjectDataForm} from '../../../forms/ProjectDataForm';
import React, {createContext, useEffect, useState} from 'react';
import {observer} from 'mobx-react';
import {BaseDialogProps} from '../../../../common/props';
import {MyLabelPanel} from './MyLabelPanel';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../store';
import {MyLabelAuthFeature} from '../../../../store/features/mylabel/auth/MyLabelAuthFeature';
import {RecipeSelection, SelectRevisionPanel} from './SelectRevisionPanel';
import {ActiveConfig, ProjectCreationConfig} from '../../../../store/agents/ProjectCreationAgent';
import {
    resetActualValues,
    resetCache,
    saveCache,
    setProjectDataValues
} from "../../../../store/slices/projectdata-slice";

interface Props extends BaseDialogProps {
    onProjectCreate: (config: ProjectCreationConfig) => void;
}

enum CreateProjDialogSteps {
    PROJECT_DATA = "project-data",
    MYLABEL = "mylabel",
    SELECT_REVISION = "select-recipe"
}

export const DialogContext = createContext({
    setNextEnabled: (value: boolean) => {
    }
})

export const CreateProjectDialog = observer((props: Props) => {
    const authData = useSelector((state: RootState) => state.authState);
    const dispatch = useDispatch();
    const projectData = useSelector((state: RootState) => state.projectCreate);

    const [nextEnabled, setNextEnabled] = useState(false);
    const [createEnabled, setCreateEnabled] = useState(false);
    const [selection, setSelection] = useState<RecipeSelection>();

    // Indica se MyLabel è stato collegato a questa company
    const [mlConnToComp, setMlConnToComp] = useState(false);

    const [createLoading, setCreateLoading] = useState(false);
    // Indica se le credenziali sono già state salvate in locale
    const [requiresCredentials, setRequiresCredentials] = useState(false);
    const [actionPerformer, setActionPerformer] = useState<FormActionRef>();

    useEffect(() => {
        (async () => {
            if (!authData.user.company)
                return;

            // Carico lo stato di connessione
            const connectionStatus = await MyLabelAuthFeature.checkConnection(
                authData.user.company?.id as number);
            setMlConnToComp(connectionStatus);

            // Carico le credenziali
            const credentials = MyLabelAuthFeature.requiresCredentials();
            setRequiresCredentials(credentials);
        })();
    }, [setMlConnToComp, authData]);

    const handleProjectCreation = () => {
        props.onProjectCreate({
            projectOptions: {
                ...projectData.actualValues
            } as any,
            contextOptions: {
                userId: authData.user.id
            },
            activeConfig: selection ? {
                companyId: selection.company.id,
                recipeId: selection.revision.id
            } as ActiveConfig : undefined
        });

        // Reset cache
        dispatch(resetCache());
        dispatch(resetActualValues());
    }

    return (
        <DialogContext.Provider value={{
            setNextEnabled: setNextEnabled
        }}>
            <MultistepDialog
                className={classNames({'bp4-dark': currentThemeIsDark()})}
                isOpen={props.isOpen}
                title="Nuovo progetto"
                canOutsideClickClose={false}
                onClose={props.onClose}
                nextButtonProps={{
                    text: 'Successivo',
                    disabled: !nextEnabled
                }}
                backButtonProps={{text: 'Precedente'}}
                finalButtonProps={{
                    text: 'Crea', onClick: handleProjectCreation,
                    disabled: !createEnabled,
                    loading: createLoading
                }}
                onChange={(newId, oldId) => {
                    if (newId !== CreateProjDialogSteps.PROJECT_DATA) actionPerformer?.submit();
                    else dispatch(saveCache());
                }}
            >
                <DialogStep
                    id={CreateProjDialogSteps.PROJECT_DATA}
                    panel={<div className="p-4">
                        <ProjectDataForm
                            initialValues={projectData.cachedValues}
                            actionPerformer={d => setActionPerformer(d)}
                            onSubmit={(v) => {
                                dispatch(setProjectDataValues(v));
                            }}
                            user={authData.user}
                        />
                    </div>}
                    title="Dati progetto"/>
                {(!mlConnToComp || requiresCredentials) &&
                    <DialogStep id={CreateProjDialogSteps.MYLABEL} panel={<MyLabelPanel
                        wasConnected={mlConnToComp}
                        setCanNext={setNextEnabled}
                        onAccessDone={() => setNextEnabled(true)}
                    />}
                                title="Collega mylabel"/>}
                {mlConnToComp &&
                    <DialogStep id={CreateProjDialogSteps.SELECT_REVISION} title="Seleziona la revisione"
                                panel={<SelectRevisionPanel
                                    myLabelConnected={!requiresCredentials}
                                    setCreateLoading={setCreateLoading}
                                    setCanCreate={setCreateEnabled}
                                    onSelectionChange={value => {
                                        setSelection(value);
                                        if (value && (!value.revision || !value.company)) setCreateEnabled(
                                            false);
                                        else setCreateEnabled(true);
                                    }}/>}/>}
            </MultistepDialog>
        </DialogContext.Provider>
    );
});
